import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import styled from '@emotion/styled';

import { useEventLogging } from '@innovamat/event-logging';
import type { Row } from '@innovamat/glimmer-components';
import {
  AnnouncementPanel,
  AvatarEditor,
  Grounder,
  HeadingSection,
  snack,
  TableComponent,
  Tabs,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';

import type {
  CreateOrUpdateStudentBody,
  Member,
  Rti,
  Student,
  StudentsQuery,
} from '@innovamat/glow-api-client';
import {
  useChangePasswordMutation,
  useDeleteEnrollmentMutation,
  useDeleteUserMutation,
  useResendInvitationMutation,
  useResetTestsMutation,
  useStudentsQuery,
  useUpdateRtiMutation,
  useUpdateStudentAvatarMutation,
} from '@innovamat/glow-api-client';
import { useKeyPress, useQueryParams } from '@innovamat/hooks';
import type { ClassroomType } from '@innovamat/radiance-utils';
import {
  getRandomNumber,
  newId,
  Stage,
  stages,
} from '@innovamat/radiance-utils';
import {
  ERROR_TYPE,
  ErrorPage,
  useCurrentNavigationValues,
} from '../../../navigation';

import { useUser } from '../../../user-management';

import {
  UpdatePeriodAnnouncementPanel,
  useCheckIsUpdatePeriod,
} from '../../../components/update-period-announcement-panel';
import { ChangePasswordModal } from '../../components/change-password-modal';
import { tableStyles } from '../../components/common-table-components/table-styles';
import { CompleteIndividualAccess } from '../../components/complete-individual-access';
import { CompleteInvitationModal } from '../../components/complete-invitation-modal';
import { ConfirmDeleteStudentModal } from '../../components/confirm-delete-student.modal';
import { HomeAccessModal } from '../../components/home-access-modal';
import { InviteParentsModal } from '../../components/invite-parents-modal';
import type { CompleteUserInvitation } from '../../components/invite-parents-modal/types';
import { InviteStudentsModal } from '../../components/invite-students-modal';
import { MoveStudentToClassroomDrawer } from '../../components/move-student-to-classroom-drawer';
import TableLegend from '../../components/table-legend/table-legend';

import { useDeleteStudent } from '../../hooks/use-delete-student';
import { useOrganization } from '../../hooks/use-organization';
import { usePermissionCourses } from '../../hooks/use-permission-courses';

import useStudentsActions from './hooks/use-students-actions';
import type { ClassroomStudent } from './types/classroom-student';
import { getStudentsColumns } from './utils/get-students-columns';
import {
  onInviteParentsAction,
  onUnlinkParentAction,
} from './utils/state-actions';

import { CLASSROOM_TYPE } from '@innovamat/radiance-utils';
import { addStudentInCache } from '../../cache-actions/add-student';
import { deleteStudentInCache } from '../../cache-actions/delete-student';
import { useStudentsEnrollment } from '../../components/add-classroom-modal/hooks/use-students-enrollment';
import { AddStudentsToClassroomModal } from '../../components/add-students-to-classroom-modal';
import { useClassroomCapabilities } from '../../hooks/use-classroom-capabilities';
import type { Classroom } from '../../modules/classroom';
import {
  DEFAULT_COURSE_ORDER_FOR_GROUPS,
  hasHomeAccess,
  isJourneyClassroom,
} from '../../modules/classroom';
import { hasDemoStudent } from '../../modules/students/students';
import { ClassroomStudentsAddDropdown } from './classroom-students-add-dropdown';
import { ClassroomStudentsTableSkeleton } from './classroom-students-table.skeleton';
import { DisableHomeAccessModal, RequestHomeAccessModal } from './components';
import { ClassroomCheckConfirmation } from './components/classroom-check-confirmation';
import { ClassroomStudentsEmptyState } from './components/classroom-students-empty-state';
import { ConfirmDeleteStudentFromGroupModal } from './components/confirm-delete-student-from-group-modal';
import { ConfirmRtiModal } from './components/confirm-rti-modal';
import { useDigitalPracticeAccess } from './hooks/use-digital-practice-access';
import { useStudents } from './hooks/use-students';
import { useStudentsPracticeLevel } from './hooks/use-students-practice-level';
import { anonymizeStudent } from './utils/anonymize-student';

const TEMPLATE_ID = 'template';

const TABS = {
  STUDENTS: 'students',
  RTI: 'rti',
} as const;
type TabsType = (typeof TABS)[keyof typeof TABS];

export type StudentRow = NonNullable<StudentsQuery['students']>[number];

const TabsPanel = styled.div`
  margin-top: 1rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const CheckClassroomAfterUpdateAnnouncementPanel = styled(AnnouncementPanel)`
  margin-top: 1rem;
`;

function ClassroomStudents(): JSX.Element {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const theme = useGlimmerTheme();

  const {
    availableLanguages = [],
    organization,
    isRosteringEnabled,
    isPlatformEnabled: isPlatformEnabledOrg,
  } = useOrganization();

  const { currentClassroom } = useCurrentNavigationValues();
  const { permissionsByCurrentCourse, arePermissionsLoaded } =
    usePermissionCourses({
      courseId: currentClassroom?.courseId,
    });

  const isJourneys = isJourneyClassroom(currentClassroom as Classroom);

  const isPlatformEnabled =
    permissionsByCurrentCourse.platformEnabled || isPlatformEnabledOrg;
  const showIntervention = permissionsByCurrentCourse.earlyIntervention;

  const { setEventData } = useEventLogging();
  const { user } = useUser();

  const classroomId = currentClassroom?.id;

  const [activeTab, setActiveTab] = useState<TabsType>(TABS.STUDENTS);

  const [changeClassroomStudent, setChangeClassroomStudent] = useState<
    ClassroomStudent | undefined
  >(undefined);

  const [currentUploadedImg, setCurrentUploadedImg] = useState<
    File | string | undefined | null
  >();

  const studentToDelete = useRef<ClassroomStudent | undefined>(undefined);
  const [showDeleteStudentModal, setShowDeleteStudentModal] = useState(false);
  const [showDeleteStudentFromGroupModal, setShowDeleteStudentFromGroupModal] =
    useState(false);

  const [inviteParentsModalId, setInviteParentsModalId] = useState<
    string | undefined
  >(undefined);

  const [isHomeAccessDisclaimer, setIsHomeAccessDisclaimer] = useState(false);
  const [isHomeAccessModalOpen, setIsHomeAccessModalOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [openAvatarEditor, setOpenAvatarEditor] = useState(false);
  const [originalRow, setOriginalRow] = useState<StudentRow>();

  const [rtiModal, setRtiModal] = useState<{
    showModal: boolean;
    isRtiEnabled: boolean;
    student?: Student;
  }>({ showModal: false, isRtiEnabled: false, student: undefined });

  const [selectedInputKey, setSelectedInputKey] = useState<string>('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<
    string | undefined
  >(undefined);

  const [showCompleteIndividualAccess, setShowCompleteIndividualAccess] =
    useState(false);

  const [showCompleteInvitation, setShowCompleteInvitation] = useState<
    CompleteUserInvitation | undefined
  >(undefined);

  const [showInviteStudentsModal, setShowInviteStudentsModal] = useState(false);
  const { students, setStudents, isPending, isError, cacheStudents } =
    useStudents();

  const [userForm, setUserForm] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    language: '',
  });

  const {
    mutate: updateStudentAvatar,
    isPending: isUpdateStudentAvatarPending,
  } = useUpdateStudentAvatarMutation();
  const { mutate: changePassword, isPending: isChangePasswordLoading } =
    useChangePasswordMutation();
  const { mutate: deleteUser } = useDeleteUserMutation();
  const { mutate: resendInvitation } = useResendInvitationMutation();
  const { mutate: resetTests } = useResetTestsMutation();
  const { mutate: updateRti, isPending: isLoadingRti } = useUpdateRtiMutation();
  const [showEnrollStudentsModal, setShowEnrollStudentsModal] = useState(false);

  const hasClassroomDigitalPracticeAccess = hasHomeAccess(currentClassroom);

  const {
    isDisableModalOpen,
    isDisablePending,
    isRequestModalOpen,
    handleCloseDisableModal,
    handleCloseRequestModal,
    handleConfirmDisable,
    handleContactAdvisor,
    handleOpenModal: handleOpenDigitalAccessConfirmationModal,
  } = useDigitalPracticeAccess({
    hasClassroomDigitalPracticeAccess,
  });

  const queryClient = useQueryClient();

  const handleSuccessDeleteStudent = (): void => {
    snack.success(t('students.toast.success.deleteStudent'));
    deleteStudentInCache({
      classroomId: classroomId!,
      organizationId: organization?.id!,
      queryClient,
      studentId: studentToDelete.current?.id!,
    });
    setStudents((prev) => {
      if (!prev) return prev;
      return prev.filter(
        (student) => student?.id !== studentToDelete.current?.id
      );
    });
  };

  const { onDeleteStudent, isDeletingLoading } = useDeleteStudent({
    onError: () => {
      snack.error(t('students.toast.delete.error'));
    },
    onSuccess: () => {
      handleSuccessDeleteStudent();
      handleCloseConfirmDeleteStudentModal();
    },
  });

  const { mutate: deleteEnrollment, isPending: isDeletingStudentFromGroup } =
    useDeleteEnrollmentMutation({
      onError: () => {
        snack.error(t('students.toast.delete-group.error'));
      },
      onSuccess: () => {
        handleSuccessDeleteStudent();
        handleCloseConfirmDeleteStudentFromGroupModal();
      },
    });

  const { handleAddStudents, failures, isLoadingStudentsMutation } =
    useStudentsEnrollment({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: useStudentsQuery.getKey({
            classroomId: classroomId!,
            organizationId,
          }),
        });
        setShowEnrollStudentsModal(false);
      },
    });

  const isUpdatePeriod = useCheckIsUpdatePeriod();

  const { createOrUpdateStudent } = useStudentsActions();

  const classroomCapabilities = useClassroomCapabilities({
    classroom: currentClassroom as Classroom,
  });
  const {
    canBeAdded: canStudentsBeAdded,
    canDemoBeAdded: canDemoStudentsBeAdded,
  } = classroomCapabilities.students;
  const hasADemoStudent = hasDemoStudent(students as ClassroomStudent[]);

  const courseOrder =
    currentClassroom?.courseOrder || DEFAULT_COURSE_ORDER_FOR_GROUPS;
  const courseId = currentClassroom?.courseId;
  const organizationId = organization?.id ?? '';

  const { practiceLevelOptions, getPracticeLevelByCourse } =
    useStudentsPracticeLevel({
      courseOrder: courseOrder ?? undefined,
      enabled: isJourneys || !!courseOrder,
    });

  const isSecundariaCourse = stages.isSecundaria(courseOrder);

  const { appLimitsByCourse, getDefaultAppLimitValue } = stages;

  const stage = stages.getStageFromOrder(
    courseOrder || DEFAULT_COURSE_ORDER_FOR_GROUPS
  );

  const allowChangeRti = useMemo(
    () =>
      Boolean(
        currentClassroom?.pilotStatus?.RTI &&
          showIntervention &&
          stage === Stage.PRIMARIA
      ),
    [currentClassroom?.pilotStatus?.RTI, showIntervention, stage]
  );

  useEffect(() => {
    if (queryParams.get('tab') === TABS.RTI && allowChangeRti) {
      setActiveTab(TABS.RTI);
    }
  }, [queryParams.get('tab'), allowChangeRti]);

  const handleCloseChangeClassroomDrawer = (): void => {
    setChangeClassroomStudent(undefined);
  };

  const handleMoveStudent = (student?: ClassroomStudent): void => {
    setChangeClassroomStudent(student);
  };

  const handleCloseConfirmDeleteStudentModal = (): void => {
    setShowDeleteStudentModal(false);
    studentToDelete.current = undefined;
  };

  const validateStudentsLength = (): boolean => {
    if (!students?.length) return true;
    return students.length < 40;
  };

  const isLengthValid = validateStudentsLength();

  const createNewStudentTemplate = (
    isDemo = false
  ): NonNullable<StudentsQuery['students']>[number] => {
    const lang =
      availableLanguages.find((l) => l.value === user?.locale) ||
      availableLanguages[0];

    const practiceLevel = courseId ? getPracticeLevelByCourse(courseId) : null;

    return {
      alias: null,
      avatar: null,
      classroomId: classroomId!,
      courseOrder: courseOrder,
      educationalPlanId: practiceLevel?.educationalPlan?.id,
      bubblePlanId: practiceLevel?.bubblePlan?.id,
      email: null,
      firstName: null,
      homeAccess: false,
      icon: null,
      id: 'template',
      individualAccess: null,
      ...(isDemo && { isDemo: true }),
      isInvitation: false,
      isSecondaryStudent: stages.isSecundaria(courseOrder),
      language: lang.value,
      lastLogin: null,
      lastName: null,
      parents: null,
      pinCode: null,
      rti: null,
      token: null,
      weeklyTimeLimit: getDefaultAppLimitValue(courseOrder),
    };
  };

  const handleAddEmptyRow = (): void => {
    if (isJourneys) {
      setShowEnrollStudentsModal(true);
      return;
    }

    if (stage === Stage.SECUNDARIA || stage === Stage.SECONDARY) {
      setShowInviteStudentsModal(true);
      return;
    }

    if (isMutating) return;

    const newStudent = createNewStudentTemplate();

    setSelectedStudentId(TEMPLATE_ID);
    setSelectedInputKey('firstName');
    setStudents((prev) => {
      if (!prev) return [newStudent];
      return [...prev, newStudent];
    });
  };

  const handleAddEmptyRowForDemoStudent = (): void => {
    if (isMutating) return;

    const newStudent = createNewStudentTemplate(true);

    setSelectedStudentId(TEMPLATE_ID);
    setSelectedInputKey('firstName');
    setStudents((prev) => {
      if (!prev) return [newStudent];
      return [newStudent, ...prev];
    });
  };

  const handleOnEnter = (): void => {
    const isDialogOpened = document.querySelector('[role="dialog"]');

    if (isDialogOpened || !canStudentsBeAdded) return;

    if (selectedStudentId === TEMPLATE_ID) {
      handleCheckRow();
    } else if (!isLengthValid) {
      snack.error(t('students.alert.maxStudentsLength'));
    } else if (selectedStudentId) {
      handleBlurInput();
    } else {
      handleAddEmptyRow();
    }
  };

  const handleCheckRow = (): void => {
    const templateStudent = students?.find(
      (student) => student?.id === TEMPLATE_ID
    );

    if (!templateStudent) return;

    if (
      selectedInputKey === 'firstName' &&
      templateStudent?.firstName &&
      !templateStudent?.lastName
    ) {
      setSelectedInputKey('lastName');
    } else if (!templateStudent?.firstName || !templateStudent?.lastName) {
      setStudents((prev) => {
        if (!prev) return prev;
        return prev.filter((student) => student?.id !== TEMPLATE_ID);
      });
      setSelectedStudentId('');
      setSelectedInputKey('');
      snack.error(t('students.toast.fillAllFields'));
    } else {
      handleCreateOrUpdateStudent(templateStudent);
    }
  };

  useKeyPress({
    Enter: handleOnEnter,
    Escape: handleCheckRow,
  });

  const weeklyTimeLimitOptions = useMemo(() => {
    const data = appLimitsByCourse(courseOrder, organization?.isLATAM);
    return data.map((time) => ({
      value: time,
      label: `${time} min`,
    }));
  }, [courseOrder, organization, appLimitsByCourse]);

  const updateIndividualAccess = (id: string, value: boolean): void => {
    updateStudentsTableData('individualAccess', value, id);
  };

  const updateStudentsTableData = (
    key: string,
    value: any,
    selectedId?: string
  ): void => {
    const id = selectedId || selectedStudentId;
    if (!id) return;

    setStudents((prev) => {
      if (!prev) return prev;

      return prev.map((student) => {
        if (student?.id === id) {
          return { ...student, [key]: value };
        }

        return student;
      });
    });
  };

  const hasStudentOrParentsEmail = (student: StudentRow): boolean => {
    return Boolean(student?.email || student?.parents?.length);
  };

  const handleInputChange = (
    key: string,
    value: any,
    selectedId?: string
  ): void => {
    const id = selectedId || selectedStudentId;
    if (!id) return;

    updateStudentsTableData(key, value, selectedId);

    let updatedStudent;

    students?.forEach((student) => {
      if (student?.id === id) {
        updatedStudent = { ...student, [key]: value };
      }
    });

    if (selectedId && updatedStudent) {
      handleCreateOrUpdateStudent(updatedStudent);
    }
  };

  const handleSelect = (studentId: string, inputKey: string): void => {
    setSelectedStudentId(studentId);
    setSelectedInputKey(inputKey);
    setOriginalRow(students?.find((student) => student?.id === studentId));
  };

  const handleCreateOrUpdateStudent = (
    studentDataChange: NonNullable<StudentsQuery['students']>[number]
  ): void => {
    const processedData = organization?.anonymized
      ? anonymizeStudent(studentDataChange)
      : studentDataChange;

    const {
      alias,
      avatar,
      bubblePlanId,
      educationalPlanId,
      firstName,
      homeAccess,
      id,
      isDemo,
      language,
      lastName,
      pinCode,
      weeklyTimeLimit,
      classroomId,
    } = processedData!;

    if (
      !id ||
      !firstName ||
      !lastName ||
      !language ||
      (!educationalPlanId && !bubblePlanId)
    ) {
      setStudents((prev) => {
        if (!prev) return prev;
        return prev.map((student) => {
          if (student?.id === id) {
            return originalRow!;
          }

          return student;
        });
      });
      snack.error(t('students.toast.fillAllFields'));
      return;
    }

    const body: CreateOrUpdateStudentBody = {
      alias: alias || null,
      classroomId: classroomId!,
      educationalPlanId: stages.isSecundaria(courseOrder)
        ? educationalPlanId!
        : null,
      bubblePlanId: !stages.isSecundaria(courseOrder) ? bubblePlanId! : null,
      firstName: firstName!,
      homeAccess: !!homeAccess,
      id: id === TEMPLATE_ID ? newId() : id!,
      language: language!,
      lastName: lastName!,
      organizationId,
      isDemo: isDemo || false,
      pinCode: pinCode || getRandomNumber(1, 99).toString(),
      studentAvatar: avatar,
      studentIcon: getRandomNumber(1, 30),
      weeklyTimeLimit: weeklyTimeLimit || +weeklyTimeLimitOptions[0].value,
    };

    setIsMutating(true);
    createOrUpdateStudent.mutate(
      {
        body,
        fromGoogleClassroom: false,
      },

      {
        onSettled: () => {
          setIsMutating(false);
        },
        onError: () => {
          snack.error(t('common.toast.error'));
          if (id === TEMPLATE_ID) {
            setStudents((prev) => {
              if (!prev) return prev;
              return prev.filter((student) => student?.id !== TEMPLATE_ID);
            });
          } else {
            setStudents((prev) => {
              if (!prev) return prev;
              return prev.map((student) => {
                if (student?.id === id) {
                  return originalRow!;
                }

                return student;
              });
            });
          }
        },
        onSuccess: () => {
          const isNewStudent = id === TEMPLATE_ID;

          if (isNewStudent) {
            addStudentInCache({
              queryClient,
              organizationId,
              classroomId: classroomId!,
            });
          }

          snack.success(t('common.toast.success'));
          setStudents((prev) => {
            if (!prev) return prev;
            return prev.map((student) => {
              if (student?.id === id) {
                return { ...student, ...body };
              }

              return student;
            });
          });
        },
      }
    );

    setSelectedStudentId('');
    setSelectedInputKey('');
  };

  const handleBlurInput = (): void => {
    if (selectedStudentId === TEMPLATE_ID) {
      handleCheckRow();
      return;
    }

    const studentData = cacheStudents?.find(
      (student) => student?.id === selectedStudentId
    );
    const studentDataChange = students?.find(
      (student) => student?.id === selectedStudentId
    );
    if (
      studentDataChange &&
      studentData?.[selectedInputKey as keyof StudentRow] !==
        studentDataChange?.[selectedInputKey as keyof StudentRow]
    ) {
      handleCreateOrUpdateStudent(studentDataChange);
    }
    setSelectedStudentId('');
    setSelectedInputKey('');
  };

  const handleOpenDigitalPracticeAccessModal = (): void => {
    setIsHomeAccessModalOpen(true);
  };

  const handleConfirmDeleteStudent = (student: ClassroomStudent): void => {
    onDeleteStudent(student);
  };

  const handleCloseHomeAccessModal = (accessType?: string): void => {
    if (accessType) {
      if (isHomeAccessDisclaimer) {
        handleInputChange('homeAccess', true, selectedStudentId);
        setSelectedStudentId('');
      }
    }

    setIsHomeAccessModalOpen(false);
    setIsHomeAccessDisclaimer(false);
  };

  const handleToggleIndividualAccess = useCallback(
    (open: boolean, id: string) => {
      const student = students?.find((student) => student?.id === id);

      if (open) {
        setShowCompleteIndividualAccess(true);
        updateIndividualAccess(id, true);
        const form = {
          id: student?.id || '',
          firstName: student?.firstName || '',
          lastName: student?.lastName || '',
          email: student?.email || '',
          password: '',
          language: student?.language || '',
        };
        setUserForm({ ...form });
      } else {
        updateIndividualAccess(id, false);
        deleteUser({ userId: id });
        setShowCompleteIndividualAccess(false);
      }
    },
    [deleteUser, students, updateIndividualAccess]
  );

  const handleIndividualAccessSuccess = (id: string, email: string): void => {
    setShowCompleteIndividualAccess(false);

    const updatedStudents = students
      ?.map((student) => {
        if (student?.email === email) {
          return { ...student, individualAccess: false, email: null };
        }
        return student;
      })
      .map((student) => {
        if (student?.id === id) {
          return { ...student, individualAccess: true, email };
        }
        return student;
      });

    setStudents(updatedStudents);
  };

  const handleIndividualAccessCancel = (): void => {
    setShowCompleteIndividualAccess(false);
    updateIndividualAccess(userForm.id, false);
  };

  const handleToggleHomeAccess = (value: boolean, studentId: string): void => {
    const student = students?.find((student) => student?.id === studentId);
    if (!student) return;

    if (value && !hasStudentOrParentsEmail(student)) {
      setSelectedStudentId(studentId);
      setInviteParentsModalId(studentId);

      return;
    }

    setOriginalRow(student);
    handleInputChange('homeAccess', value, studentId);
  };

  const handleInviteParents = (studentId: string): void => {
    setInviteParentsModalId(studentId);
  };

  const handleOnInviteParents = (
    parents: Member[],
    studentId: string
  ): void => {
    if (!studentId) return;

    onInviteParentsAction({
      parents,
      studentId,
      setStudents,
    });

    const student = students?.find((student) => student?.id === studentId);
    if (!student?.homeAccess) {
      handleInputChange('homeAccess', true, studentId);
    }
  };

  const handleCloseInviteParentsModal = (hasParentsEmails = false): void => {
    setInviteParentsModalId(undefined);
    const student = students?.find(
      (student) => student?.id === inviteParentsModalId
    );

    if (
      student?.homeAccess &&
      !hasParentsEmails &&
      !hasStudentOrParentsEmail(student)
    ) {
      handleInputChange('homeAccess', false, inviteParentsModalId);
    }
  };

  const handleUnlinkParent = (parent: Member, student: StudentRow): void => {
    const studentId = student?.id!;

    onUnlinkParentAction({
      studentId,
      parentEmail: parent.userInfo?.email || '',
      setStudents,
    });
  };

  const handleSuccessMoveStudent = (student: Student): void => {
    setStudents((prev) => {
      if (!prev) return prev;
      return prev.filter((s) => s?.id !== student.id);
    });
  };

  const handleCompleteInvitationSuccess = (): void => {
    addStudentInCache({
      queryClient,
      organizationId: organization?.id!,
      classroomId: classroomId!,
    });
    setShowCompleteInvitation(undefined);
  };

  const handleToggleRti = (studentId: string, value: boolean): void => {
    setRtiModal({
      showModal: true,
      isRtiEnabled: value,
      student: students.find((student) => student?.id === studentId)!,
    });
  };

  const handleCloseRtiModal = (): void => {
    setRtiModal({
      showModal: false,
      isRtiEnabled: false,
      student: undefined,
    });
  };

  const handleUpdateRti = (): void => {
    if (!rtiModal.student?.id) return;

    const onUpdateStudentInState = (rti: Rti): void => {
      setStudents((prevState) => {
        return prevState?.map((student) => {
          if (student?.id === rtiModal.student?.id) {
            return {
              ...student,
              rti,
            };
          }
          return student;
        });
      });
    };

    updateRti(
      {
        body: {
          studentId: rtiModal.student?.id,
          enabled: rtiModal.isRtiEnabled,
        },
      },
      {
        onError: () => {
          handleCloseRtiModal();
        },
        onSuccess: (response) => {
          const event = rtiModal.isRtiEnabled
            ? 'rti_activate'
            : 'rti_deactivate';

          setEventData(event, {
            classroom_id: classroomId,
            course: courseOrder,
            student_id: rtiModal.student?.id,
            school_id: organizationId,
          });

          handleCloseRtiModal();
          onUpdateStudentInState({
            enabled: response.updateRti?.enabled,
            startDate: response.updateRti?.startDate,
            endDate: response.updateRti?.endDate,
          });
        },
      }
    );
  };

  const handleResetTests = (student: ClassroomStudent | undefined): void => {
    resetTests(
      {
        body: {
          studentId: student?.id || '',
          classroomId: classroomId!,
        },
      },
      {
        onError: () => {
          snack.error(t('resetTests.error'));
        },
        onSuccess: () => {
          snack.success(t('resetTests.success'));
        },
      }
    );
  };

  const handleDeleteStudent = (student: ClassroomStudent | undefined): void => {
    if (!student) return;
    setShowDeleteStudentModal(true);
    studentToDelete.current = student;
  };

  const handleChangePassword = (password: string): void => {
    const userId = showChangePasswordModal;

    changePassword(
      { id: userId!, password },
      {
        onSuccess: () => {
          snack.success(t('common.toast.success.updatePassword'));
          setShowChangePasswordModal(undefined);
        },
      }
    );
  };

  const handleOpenChangePasswordModal = (studentId: string): void => {
    setShowChangePasswordModal(studentId);
  };

  const handleUploadImg = (imageFile: File | string | undefined): void => {
    setCurrentUploadedImg(imageFile);
  };

  const handleOpenAvatarEditor = (
    id: string,
    img: string | null | undefined
  ): void => {
    handleSelect(id, 'avatar');
    setCurrentUploadedImg(img);
    setOpenAvatarEditor(true);
  };

  const handleCloseAvatarEditor = (): void => {
    setCurrentUploadedImg(undefined);
    setOpenAvatarEditor(false);
  };

  const handleSaveImg = (image: string): void => {
    updateStudentAvatar(
      {
        body: {
          id: selectedStudentId,
          image,
        },
      },
      {
        onSuccess: () => {
          handleInputChange('avatar', image);
          snack.success(t('common.toast.success'));
        },
        onError: () => {
          snack.error(t('common.toast.error'));
        },
        onSettled: () => {
          handleCloseAvatarEditor();
        },
      }
    );
  };

  const handleResendInvitation = (studentId: string): void => {
    const student = students.find((student) => student?.id === studentId);
    if (!student?.email) return;
    resendInvitation(
      { body: { token: student.token!, id: student.id! } },
      {
        onSuccess: () => {
          snack.success(t('userStatus.toast.success.invitationForwarded'));
        },
        onError: () => {
          snack.error(t('students.toast.error'));
        },
      }
    );
  };

  const handleOpenCompleteManuallyModal = (studentId: string): void => {
    const student = students.find((student) => student?.id === studentId);
    if (!student) return;

    setShowCompleteInvitation({
      email: student.email!,
      id: student.id!,
      token: student.token!,
    });
  };

  const handleMoveStudentToGroup = (
    student: ClassroomStudent | undefined
  ): void => {
    setChangeClassroomStudent(student);
  };

  const handleDeleteStudentFromGroup = (
    student: ClassroomStudent | undefined
  ): void => {
    setShowDeleteStudentFromGroupModal(true);
    studentToDelete.current = student;
  };

  const handleConfirmDeleteStudentFromGroup = (
    student: ClassroomStudent | undefined
  ): void => {
    if (!student) return;

    const enrollmentId = student.enrollments.find(
      (enrollment) => enrollment.classroomType === CLASSROOM_TYPE.JOURNEYS
    )?.id;

    if (!enrollmentId) return;

    deleteEnrollment({ enrollmentId });
  };

  const handleCloseConfirmDeleteStudentFromGroupModal = (): void => {
    setShowDeleteStudentFromGroupModal(false);
    studentToDelete.current = undefined;
  };

  const filteredAvailableLanguages =
    stage === Stage.SECUNDARIA || stage === Stage.SECONDARY
      ? availableLanguages.filter((lang) => lang.value !== 'va')
      : availableLanguages; // Remove valencian language for secondary

  const columns = getStudentsColumns({
    availableLanguages: filteredAvailableLanguages,
    hasClassroomDigitalPracticeAccess,
    classroomCapabilities,
    classroomHasHomeAccess: hasClassroomDigitalPracticeAccess,
    practiceLevelOptions,
    isRti: activeTab === TABS.RTI && allowChangeRti,
    isAnonymized: organization?.anonymized!,
    isRosteringEnabled,
    selectedInputKey,
    selectedStudentId,
    stage,
    weeklyTimeLimitOptions,
    isJourneys,
    handleBlurInput,
    handleDeleteStudent,
    handleInputChange,
    handleInviteParents,
    handleMoveStudent,
    handleOpenAvatarEditor,
    handleOpenChangePasswordModal,
    handleOpenCompleteManuallyModal,
    handleOpenDigitalPracticeAccessModal,
    handleOpenDigitalAccessConfirmationModal,
    handleResendInvitation,
    handleResetTests,
    handleSelect,
    handleToggleHomeAccess,
    handleToggleIndividualAccess,
    handleToggleRti,
    handleMoveStudentToGroup,
    handleDeleteStudentFromGroup,
    t,
  });

  useEffect(() => {
    if (queryParams.get('tab') === TABS.RTI && allowChangeRti) {
      setActiveTab(TABS.RTI);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.get('tab'), allowChangeRti]);

  if (arePermissionsLoaded && !isPlatformEnabled) {
    return <Navigate to="/" />;
  }

  if (!currentClassroom) {
    return <></>;
  }

  if (isError) {
    return <ErrorPage errorType={ERROR_TYPE.NOT_AVAILABLE} />;
  }

  return (
    <>
      <HeadingSection
        title={t('students.title')}
        actions={
          <ClassroomStudentsAddDropdown
            isDisabled={isUpdatePeriod || !isPlatformEnabled}
            handleAdd={handleAddEmptyRow}
            isJourneys={isJourneys}
            showAddDemoStudent={!hasADemoStudent && canDemoStudentsBeAdded}
            onAddDemoStudent={handleAddEmptyRowForDemoStudent}
          />
        }
      />

      <UpdatePeriodAnnouncementPanel />

      {!currentClassroom?.checked && (
        <CheckClassroomAfterUpdateAnnouncementPanel
          text={
            <Trans
              i18nKey="classroom.updatePeriod.announcement"
              components={{
                a: <></>,
              }}
            />
          }
          type="imported"
          canClose={false}
        />
      )}

      {allowChangeRti && (
        <TabsPanel>
          <Tabs
            tabs={[
              {
                selected: activeTab === TABS.STUDENTS,
                text: t('students.tabs.personal-information'),
                onClick: () => setActiveTab(TABS.STUDENTS),
              },
              {
                selected: activeTab === TABS.RTI,
                text: t('students.table.labels.intervention'),
                onClick: () => setActiveTab(TABS.RTI),
              },
            ]}
          />
        </TabsPanel>
      )}
      {isPending ? (
        <ClassroomStudentsTableSkeleton
          hasClassroomDigitalPracticeAccess={hasClassroomDigitalPracticeAccess}
          stage={stage}
          isAnonymized={organization?.anonymized! && stage !== Stage.SECUNDARIA}
        />
      ) : cacheStudents?.length === 0 && students.length === 0 ? (
        <ClassroomStudentsEmptyState
          handleAddStudent={handleAddEmptyRow}
          isDisabled={isUpdatePeriod || !isPlatformEnabled}
          isJourneyClassroom={isJourneys}
          isRosteringEnabled={isRosteringEnabled}
        />
      ) : (
        <>
          <TableComponent
            backgroundColor="transparent"
            columns={columns}
            id="classroom-students-table"
            isDisabled={isUpdatePeriod}
            rows={students as Row[]}
            shouldHighlightRow={(row) => row.isDemo}
            sx={tableStyles(theme)}
          />
          {!isRosteringEnabled && !isJourneys && <TableLegend />}
        </>
      )}

      <HomeAccessModal
        classroomId={classroomId!}
        isDisclaimer={isHomeAccessDisclaimer}
        isOpen={isHomeAccessModalOpen}
        onClose={(accessType) => handleCloseHomeAccessModal(accessType)}
        organizationId={organizationId}
      />
      <CompleteIndividualAccess
        handleInput={handleInputChange}
        initForm={userForm}
        onClose={(cancel) =>
          cancel
            ? handleIndividualAccessCancel()
            : setShowCompleteIndividualAccess(false)
        }
        onSuccess={handleIndividualAccessSuccess}
        showModal={showCompleteIndividualAccess}
      />
      <InviteParentsModal
        classroom={currentClassroom as Classroom}
        onClose={handleCloseInviteParentsModal}
        onCompleteManually={setShowCompleteInvitation}
        onInviteParents={handleOnInviteParents}
        onUnlinkParent={handleUnlinkParent}
        showModal={Boolean(inviteParentsModalId)}
        student={students.find(
          (student) => student?.id === inviteParentsModalId
        )}
      />

      <MoveStudentToClassroomDrawer
        onClose={handleCloseChangeClassroomDrawer}
        onSuccess={handleSuccessMoveStudent}
        showDrawer={Boolean(changeClassroomStudent)}
        student={changeClassroomStudent}
        classroomType={currentClassroom?.type as ClassroomType}
      />

      <ConfirmDeleteStudentModal
        isLoading={isDeletingLoading}
        isOpen={showDeleteStudentModal}
        onConfirm={() =>
          studentToDelete.current &&
          handleConfirmDeleteStudent(studentToDelete.current)
        }
        onDismiss={handleCloseConfirmDeleteStudentModal}
        student={studentToDelete.current}
      />

      <ConfirmDeleteStudentFromGroupModal
        isLoading={isDeletingStudentFromGroup}
        isOpen={showDeleteStudentFromGroupModal}
        onConfirm={() =>
          studentToDelete.current &&
          handleConfirmDeleteStudentFromGroup(studentToDelete.current)
        }
        onDismiss={handleCloseConfirmDeleteStudentFromGroupModal}
        student={studentToDelete.current}
      />

      <CompleteInvitationModal
        invitation={showCompleteInvitation}
        onClose={() => setShowCompleteInvitation(undefined)}
        onSuccess={handleCompleteInvitationSuccess}
        showModal={Boolean(showCompleteInvitation)}
        type={isSecundariaCourse ? 'user' : 'parent'}
      />
      <ConfirmRtiModal
        isLoadingRti={isLoadingRti}
        isOpen={rtiModal.showModal}
        onClose={handleCloseRtiModal}
        onConfirm={handleUpdateRti}
        rtiEnabled={rtiModal.isRtiEnabled}
      />

      <AvatarEditor
        img={currentUploadedImg}
        isLoading={isUpdateStudentAvatarPending}
        onClose={handleCloseAvatarEditor}
        onSaveImg={handleSaveImg}
        onUploadImg={handleUploadImg}
        open={openAvatarEditor}
        t={t}
      />
      <ChangePasswordModal
        loading={isChangePasswordLoading}
        onClose={() => setShowChangePasswordModal(undefined)}
        onSubmit={handleChangePassword}
        open={Boolean(showChangePasswordModal)}
      />
      <InviteStudentsModal
        classroomId={currentClassroom.id!}
        classroomName={currentClassroom?.name!}
        onClose={() => setShowInviteStudentsModal(false)}
        showModal={showInviteStudentsModal}
      />
      <RequestHomeAccessModal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        onContactAdvisor={handleContactAdvisor}
      />
      <DisableHomeAccessModal
        isOpen={isDisableModalOpen}
        isPending={isDisablePending}
        onClose={handleCloseDisableModal}
        onConfirm={handleConfirmDisable}
      />

      <AddStudentsToClassroomModal
        isLoading={isLoadingStudentsMutation}
        failures={failures}
        show={showEnrollStudentsModal}
        onClose={() => setShowEnrollStudentsModal(false)}
        onSave={(emails) => {
          handleAddStudents({
            emails,
            classroomBody: currentClassroom,
          });
        }}
      />

      <Grounder />
      {!currentClassroom?.checked && (
        <ClassroomCheckConfirmation classroom={currentClassroom} />
      )}
    </>
  );
}

export { ClassroomStudents };
